import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  ListItemText,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import { useWarbandStore } from '../../stores/warbandStore';
import { useStargraveCustomStore, useStargraveEquipment } from '../../stores/stargraveCustomStore';
import { StargraveSoldierProfile } from '../../types';
import { formatStargraveSoldierCategory } from '../../game/stargraveSoldierRules';
import { CustomContentManager } from '../shared/CustomContentManager';

interface FormState {
  name: string;
  category: 'standard' | 'specialist' | 'creature';
  move: string;
  fight: string;
  shoot: string;
  armour: string;
  will: string;
  health: string;
  cost: string;
  equipmentIds: string[];
  notes: string;
}

const emptyForm = (): FormState => ({
  name: '',
  category: 'standard',
  move: '6',
  fight: '2',
  shoot: '2',
  armour: '10',
  will: '0',
  health: '12',
  cost: '0',
  equipmentIds: [],
  notes: '',
});

function profileToForm(profile: StargraveSoldierProfile): FormState {
  return {
    name: profile.name,
    category: profile.category,
    move: String(profile.move),
    fight: String(profile.fight),
    shoot: String(profile.shoot),
    armour: String(profile.armour),
    will: String(profile.will),
    health: String(profile.health),
    cost: String(profile.cost),
    equipmentIds: [...profile.equipmentIds],
    notes: profile.notes ?? '',
  };
}

function parseNum(value: string, fallback = 0): number {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
}

const SoldierFormFields: React.FC<{
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}> = ({ form, setForm }) => {
  const allEquipment = useStargraveEquipment();
  const setField = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 8 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setField('name', e.target.value)} fullWidth required size="small" />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={form.category} onChange={(e) => setField('category', e.target.value as FormState['category'])}>
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="specialist">Specialist</MenuItem>
            <MenuItem value="creature">Creature</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {([['move', 'Move'], ['fight', 'Fight'], ['shoot', 'Shoot'], ['armour', 'Armour'], ['will', 'Will'], ['health', 'Health']] as const).map(([key, label]) => (
        <Grid size={{ xs: 4, sm: 2 }} key={key}>
          <TextField label={label} type="number" value={form[key]} onChange={(e) => setField(key, e.target.value)} fullWidth size="small" />
        </Grid>
      ))}
      <Grid size={{ xs: 4 }}>
        <TextField label="Cost (cr)" type="number" value={form.cost} onChange={(e) => setField('cost', e.target.value)} fullWidth size="small" />
      </Grid>
      <Grid size={{ xs: 8 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Equipment</InputLabel>
          <Select
            label="Equipment"
            multiple
            value={form.equipmentIds}
            onChange={(e) => setField('equipmentIds', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            renderValue={(selected) => selected.map((id) => allEquipment.find((item) => item.id === id)?.name ?? id).join(', ')}
          >
            {allEquipment.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                <Checkbox checked={form.equipmentIds.includes(item.id)} size="small" />
                <ListItemText primary={item.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField label="Special Rules / Notes" value={form.notes} onChange={(e) => setField('notes', e.target.value)} fullWidth multiline minRows={2} size="small" />
      </Grid>
    </Grid>
  );
};

export const ManageStargraveBestiaryModal: React.FC = () => {
  const { ui, closeCustomBestiaryModal } = useWarbandStore();
  const customSoldiers = useStargraveCustomStore((s) => s.customSoldiers);
  const addCustomSoldier = useStargraveCustomStore((s) => s.addCustomSoldier);
  const updateCustomSoldier = useStargraveCustomStore((s) => s.updateCustomSoldier);
  const deleteCustomSoldier = useStargraveCustomStore((s) => s.deleteCustomSoldier);

  return (
    <Dialog open={ui.customBestiaryModalOpen} onClose={closeCustomBestiaryModal} maxWidth="sm" fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle>Custom Bestiary</DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', flex: 1 }}>
        {ui.customBestiaryModalOpen && <CustomContentManager<StargraveSoldierProfile, FormState>
          itemLabel="Creature"
          emptyMessage="No custom creatures yet."
          items={customSoldiers}
          getItemName={(profile) => profile.name}
          getItemSecondary={(profile) => `${formatStargraveSoldierCategory(profile.category)} · ${profile.cost}cr · M${profile.move} F${profile.fight >= 0 ? '+' : ''}${profile.fight} S${profile.shoot >= 0 ? '+' : ''}${profile.shoot} A${profile.armour} W${profile.will >= 0 ? '+' : ''}${profile.will} H${profile.health}`}
          emptyForm={emptyForm}
          itemToForm={profileToForm}
          validate={(form) => (form.name.trim() ? null : 'Name is required.')}
          renderFormFields={(form, setForm) => <SoldierFormFields form={form} setForm={setForm} />}
          onSave={(form, editingId) => {
            const data: Omit<StargraveSoldierProfile, 'id' | 'isCustom'> = {
              name: form.name.trim(),
              category: form.category,
              move: parseNum(form.move, 6),
              fight: parseNum(form.fight, 2),
              shoot: parseNum(form.shoot, 2),
              armour: parseNum(form.armour, 10),
              will: parseNum(form.will, 0),
              health: parseNum(form.health, 12),
              cost: parseNum(form.cost, 0),
              equipmentIds: form.equipmentIds,
              notes: form.notes.trim() || undefined,
              sourcePage: 0,
            };
            if (editingId) updateCustomSoldier(editingId, data);
            else addCustomSoldier(data);
          }}
          onDelete={deleteCustomSoldier}
          deleteMessage={(profile) => `Delete "${profile.name}"? Existing crew members using this profile will no longer display. This cannot be undone.`}
        />}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCustomBestiaryModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
